
import { computed, defineComponent, onUpdated, reactive, ref, Ref, watch } from "vue";
import $API from "@/services";
import FileUploader from "@/components/common/FileUploader.vue";
import Textarea from "primevue/textarea";
import { IAlert, IDoctorScheduled, ITimeList } from "@/models/common";
import { IDoctor, PossibleTypes } from "@/models/doctor";
import { useStore } from "@/store";
import { CommonMutationTypes } from "@/store/modules/common/mutations";
import { IRequest, RequestKindTypes, RequestStatuses } from "@/models/request";
import { IFileListItem } from "@/models/file";
import FileList from "@/components/file/FileList.vue";
import { DataType, FileType } from "@/services/file";
import Sidebar from "primevue/sidebar";
import Doctors from "@/components/common/Doctors/Doctors.vue";
import DateFormat from "@/utils/date-format";
import { IOnlineCarePurpose, OnlineCare } from "@/models/onlineCare";
import DoctorScheduled from "@/components/onlineCare/DoctorScheduled.vue";
import CustomConfirmDialog from "@/components/primevueCustom/ConfirmDialog.vue";
import router from "@/router";
import { Form } from "vee-validate";
import Button from "primevue/button";
import { useI18n } from "vue-i18n";
import FileViewer from "@/components/file/FileViewer.vue";
//import PharmacyList from "@/components/common/PharmacyList.vue";
import { messageType } from "@/models/message";
import PageFrame from "@/components/common/PageFrame.vue";
import RequestStep from "@/components/common/Request/RequestStep.vue";
import { getFacilityDoctorList } from "@/services/doctor";
import moment from "moment";
import RequestConfirm from "@/components/common/Confirm/RequestConfirm";
import DoctorItemOptions from "@/components/common/Doctors/DoctorItemOptions.vue";

enum PageStates {
  "DOCTOR",
  "SCHEDULE",
  "PURPOSE",
}

export default defineComponent({
  name: "Online Care Request",
  props: {
    //내용을 보여줄것인가
    isContent: {
      type: Boolean,
      default: true,
    },
    // hospitalKey: {
    //   type: Number,
    //   required: false,
    // },
  },
  setup(props) {
    const timeList: ITimeList = reactive({
      am: [
        //   "08:00",
        //   "08:20",
        //   "08:40",
        // "09:00",
        "09:20",
        "09:40",
        "10:00",
        "10:20",
        "10:40",
        "11:00",
        "11:20",
        "11:40",
        "12:00",
        "12:20",
        "12:40",
      ],
      pm: [
        "14:20",
        "14:40",
        "15:00",
        "15:20",
        "15:40",
        "16:00",
        // "16:20",
        // "16:40",
        // "17:00",
        // "17:20",
        // "17:40",
      ],
    });

    const doctorScheduled: IDoctorScheduled = reactive({
      DoctorScheduledDays: "",
      ReqScheduleds: [],
    });

    const doctors: Ref<IDoctor> = ref({} as IDoctor);

    const state = reactive({
      id: "",
      page: PageStates.DOCTOR,
      purpose: {
        date: "",
        time: "",
        doctorKey: null as Number | null,
        deptKey: null as Number | null,
        other: "",
        files: null as Array<IFileListItem> | null,
        pharmacyKey: null as Number | null,
        hospitalKey: null as Number | null,
        otherComment: "",
      } as IOnlineCarePurpose,
      requestStatus: null as RequestStatuses | null,
      selectFile: null as IFileListItem | null,
      visibleFull: false,
      isLoading: true,
      message: "",
      isContent: props.isContent,
      //selectedHospital: props.hospitalKey,
    });

    const formValidate = reactive({
      time: false,
      date: false,
      doctorKey: false,
      other: false,
    });

    const store = useStore();

    const alert: Ref<IAlert> = ref({
      type: messageType.ERROR,
      content: "",
    });

    const confirm$: Ref<RequestConfirm | null> = ref(null);

    watch(
      () => state.purpose,
      (n: IOnlineCarePurpose) => {
        formValidate.doctorKey = !!n.doctorKey;
        formValidate.date = !!n.date;
        formValidate.time = !!n.time;
        formValidate.other = !!n.other;
      },
      { deep: true }
    );

    const url = computed(() => {
      return `${$API.BaseURL}/api/ApiFileUploadV2/Upload?reftab=request&refkey=${state.id}`;
    });

    const filesCount = computed(() => {
      return state.purpose.files?.filter((f) => f.PrescriptionFlag !== 1 && f.FileStatus === 0)
        .length;
    });

    const isEditable = computed(() => {
      return (
        !state.id ||
        ((state.requestStatus ?? 100) >= RequestStatuses.Preparing &&
          (state.requestStatus ?? 100) < RequestStatuses.Scheduled)
      );
    });

    const { t } = useI18n({ useScope: "global" });

    const doctorKeyValidate = computed(() => formValidate.doctorKey);
    const scheduleValidate = computed(() => !!formValidate.date && !!formValidate.time);
    const otherValidate = computed(() => formValidate.other);
    const isValidate = computed(() => {
      if (state.page === PageStates.DOCTOR) return doctorKeyValidate.value;
      else if (state.page === PageStates.SCHEDULE) return scheduleValidate.value;
      else return doctorKeyValidate.value && otherValidate.value;
    });

    const totalPages = computed(() => Object.keys(PageStates).length / 2);

    const next = () => {
      state.page += 1;
    };
    const prev = () => {
      state.page -= 1;
    };

    const goBack = () => {
      state.page = PageStates.DOCTOR;
      router.go(-1);
    };

    // 선택된 병원
    const selectedHospital = computed(() =>
      !!doctors.value && !!doctors.value?.selectedFacility
        ? doctors.value.selectedFacility
        : undefined
    );

    // 선택된 의사
    const selectedDoctor = computed(() =>
      !!doctors.value && !!doctors.value?.selectedDoctor ? doctors.value.selectedDoctor[0] : null
    );

    // 예약 일시
    const selectedDate = computed(() =>
      moment(new Date(state.purpose.date.toString())).format("YYYY-MM-DD")
    );

    onUpdated(() => {
      if (props.isContent !== state.isContent) state.isContent = props.isContent;
    });

    return {
      state,
      doctorScheduled,
      PageStates,
      timeList,
      store,
      DataType,
      url,
      isEditable,
      formValidate,
      FileType,
      RequestStatuses,
      t,
      alert,
      filesCount,
      PossibleTypes,
      RequestKindTypes,
      totalPages,
      doctors,
      next,
      prev,
      isValidate,
      selectedHospital,
      selectedDoctor,
      selectedDate,
      RequestConfirm,
      goBack,
      confirm$,
    };
  },
  async mounted() {
    await this.onSetData();
  },
  methods: {
    // updatePharmacyKey(key: number | null) {
    //   this.state.purpose.pharmacyKey = key ?? 0;
    // },
    async saveConfirm() {
      this.$confirm.require({
        header: this.t("ReceptionSaveHeader"),
        acceptLabel: this.t("Yes"),
        rejectLabel: this.t("No"),
        group: "save",
        accept: async () => {
          await this.onSave();
        },
        reject: () => {},
      });
    },

    async deletedFile() {
      await this.onSetData();
    },

    filesProgress() {
      if (!this.store.getters.getIsFileUpdate) {
        setTimeout(() => {
          // this.routerReplace();
          this.confirm$?.isConfirmButton();
        }, 1000);
      }
    },
    async onSetData() {
      try {
        this.state.isLoading = true;
        this.store.commit(CommonMutationTypes.SET_IS_LODING, true);
        //this.state.purpose.pharmacyKey = -1;

        try {
          this.doctors.doctorList = await getFacilityDoctorList(
            PossibleTypes.ONLINE_CARE,
            this.selectedHospital?.FacilityKey
          );
        } catch (ex) {
          console.error(ex);
        }

        this.doctors.doctorListSplit = [this.doctors.doctorList];
        this.doctors.selectedPage = 0;
        this.doctors.displayDoctorRow = 0;

        await Promise.all(
          (this.doctors.selectedDoctor = this.doctors.doctorList.filter((x) => {
            if (x.DoctorKey === this.state.purpose.doctorKey) {
              return x;
            }
          }))
        );
        await this.getDoctorList();
      } catch (e) {
        this.store.commit(CommonMutationTypes.FILE_UPDATE, false);
        console.error(e);
      } finally {
        this.store.commit(CommonMutationTypes.SET_IS_LODING, this.store.getters.getIsFileUpdate);

        this.state.isLoading = false;
      }
    },
    saveValidate() {
      return (
        this.formValidate.time &&
        this.formValidate.date &&
        this.formValidate.doctorKey &&
        this.formValidate.other
      );
    },

    async onSave() {
      try {
        this.store.commit(CommonMutationTypes.SET_IS_LODING, true);
        if (!this.saveValidate()) {
          this.alert = {
            content: "validateError",
            type: messageType.ERROR,
          };

          this.store.commit(CommonMutationTypes.SET_IS_LODING, false);
          return;
        }
        const datetime = DateFormat.StringDateTime(
          this.state.purpose.date.toString(),
          this.state.purpose.time.toString()
        );

        const requestKey = !this.state.id ? 0 : Number.parseInt(this.state.id);

        const saveData = new OnlineCare({
          datetime,
          other: this.state.purpose.other,
          doctorkey: this.state.purpose.doctorKey?.valueOf() ?? 0,
          deptKey: this.state.purpose.deptKey?.valueOf() ?? 0,
          requestKey,
          hospitalKey: this.state.purpose.hospitalKey?.valueOf(),
          appointmentType: 0,
          //pharmacyKey: this.state.purpose.pharmacyKey?.valueOf() ?? 0,
          //hospitalKey: this.selectedHospital?.FacilityKey ?? null,
        });
        const req = await $API.RequestService.save(saveData);

        if (req) {
          this.state.id = req.RequestKey + "";
          this.state.requestStatus = req.RequestStatus;
          if ((this.$refs.fileUploader$ as any).getFilesCount() > 0)
            await (this.$refs.fileUploader$ as any).upload(this.url);
          else {
            this.store.commit(CommonMutationTypes.SET_IS_LODING, false);
            this.confirm$?.isConfirmButton();
          }
        }
      } catch (e) {
        console.error(e);

        this.alert = {
          content: "저장 실패",
          type: messageType.ERROR,
        };
      } finally {
        this.store.commit(CommonMutationTypes.SET_IS_LODING, false);
      }
    },

    async onSelectedDoctor(doctor: IDoctor) {
      this.doctors = doctor;
      this.state.purpose.doctorKey = doctor.selectedDoctor[0].DoctorKey;
      this.state.purpose.date = "";
      this.state.purpose.time = "";

      try {
        const docScheduled = await this.getDoctorScheduled(this.state.purpose.doctorKey);
        this.doctorScheduled.DoctorScheduledDays = docScheduled.DoctorScheduledDays ?? "";
        this.doctorScheduled.ReqScheduleds = docScheduled.ReqScheduleds;
      } catch (ex) {
        console.error(ex);
      }
    },

    async onSelectedRow(selectedItem: IFileListItem) {
      try {
        this.state.selectFile = selectedItem;
        if (this.state.selectFile.DataType === DataType.DICOM)
          await this.getStudyKeys(this.state.selectFile.FileKey.toString());

        this.state.visibleFull = true;
      } catch (e) {
        console.error(e);
      }
    },
    async getStudyKeys(fileKey: string) {
      return await $API.FileService.getStudyKeys(fileKey);
    },
    async getDoctorScheduled(doctorKey: Number) {
      return await $API.DoctorService.getScheduled(doctorKey, RequestKindTypes.OnlineCare);
    },
    submit(e: Event) {
      e.preventDefault();
    },
    // async getRequest(id: string) {
    //   const res: AxiosResponse<IResponseData<IRequest>> =
    //     await $API.RequestService.getRequest(Number.parseInt(id));
    //   const {
    //     data: { ResultData },
    //   } = res;
    //   if (!Array.isArray(ResultData)) {
    //     this.state.purpose.other = ResultData.PurposeOthers.toString() ?? "";
    //     this.state.requestStatus = ResultData.RequestStatus ?? null;
    //     this.state.purpose.pharmacyKey = ResultData.PharmacyKey;
    //     this.state.purpose.otherComment = ResultData?.OtherComments;

    //     const schedul = DateFormat.splitDatetime(
    //       (
    //         ResultData.ScheduledDateTime ?? ResultData.DesiredDateTime1
    //       ).toString()
    //     );
    //     this.state.purpose.date = schedul.date;
    //     this.state.purpose.time = schedul.time;
    //     await this.getRequestDoctor(ResultData);
    //     await this.getDoctorScheduled(ResultData.DesiredDrKey);
    //   }
    // },
    async getFiles(id: string) {
      const ResultData = await $API.FileService.getFileList(id);
      if (!Array.isArray(ResultData)) return;
      this.state.purpose.files = ResultData;
    },
    async getRequestDoctor(resultData: IRequest) {
      this.state.purpose.doctorKey = resultData.DesiredDrKey;
    },

    async getDoctorList() {
      await (this.$refs?.doctors$ as any)?.onSetData();
    },
  },

  components: {
    FileUploader,
    Textarea,
    FileList,
    Sidebar,
    Doctors,
    DoctorScheduled,
    Form,
    CustomConfirmDialog,
    Button,
    FileViewer,
    //PharmacyList,
    PageFrame,
    RequestStep,
    DoctorItemOptions,
    RequestConfirm,
  },
});
